import { EyeOutlined, EditOutlined, QuestionCircleOutlined, DeleteOutlined } from '@ant-design/icons';
import { getImportTableData } from '@app/api/import.api';
import { BaseButton } from '@app/components/common/BaseButton/BaseButton';
import { BaseCard } from '@app/components/common/BaseCard/BaseCard';
import { BaseTable } from '@app/components/common/BaseTable/BaseTable';
import { BaseButtonsForm } from '@app/components/common/forms/BaseButtonsForm/BaseButtonsForm';
import FilterComponent from '@app/components/TableFilter/TableFilter';
import { notificationController } from '@app/controllers/notificationController';
import { useMounted } from '@app/hooks/useMounted';
import { Pagination } from '@app/models/Pagination';
import { ImportTableRow } from '@app/models/import/ImportResponse';
import { TablePaginationConfig, Space, Tooltip, Button, Popconfirm } from 'antd';
import { ColumnsType } from 'antd/lib/table';
import React, { useCallback, useEffect, useState, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

const initialPagination: Pagination = {
  current: 1,
  pageSize: 10,
};

const ImportTable = () => {
  const [tableData, setTableData] = useState<{
    data: ImportTableRow[];
    pagination: Pagination;
    loading: boolean;
  }>({
    data: [],
    pagination: initialPagination,
    loading: false,
  });

  const { isMounted } = useMounted();
  const navigate = useNavigate();
  const location = useLocation();
  const [form] = BaseButtonsForm.useForm();
  const [filters, setFilters] = useState<{ [key: string]: string | [string, string] | null }>({});
  const [statusOptions, setStatusOptions] = useState<{ label: string; value: string }[] | null>(null);
  const lastFetchedRef = useRef(false);
  const selectedImportType = localStorage.getItem('selected_import_type');
  const lastPathnameRef = useRef<string | null>(null);

  const TITLE_MAP: Record<string, string> = {
    NORMAL: 'Imports - Normal',
    NON_COMPLIANCE_1: 'Imports - Non-Compliance 1',
    NON_COMPLIANCE_3: 'Imports - Non-Compliance 2',
    NON_RELEVANT: 'Imports - Non-Relevant',
    NO_OBJECTION: 'Imports - No Objection',
  };

  const title = TITLE_MAP[selectedImportType || 'NORMAL'] || 'Imports';

  const fetch = useCallback(
    (pagination: Pagination) => {
      setTableData((tableData) => ({ ...tableData, loading: true }));

      getImportTableData({
        ...pagination,
        search: form.getFieldValue('search'),
        type: selectedImportType || 'NORMAL',
      })
        .then((res) => {
          if (isMounted.current) {
            setTableData({
              data: res.data,
              pagination: {
                current: res.pagination.current,
                pageSize: res.pagination.pageSize,
                total: res.pagination.total,
              },
              loading: false,
            });
            setStatusOptions(res.status_options);
          }
        })
        .catch((error: any) => {
          setTableData({ data: [], pagination: initialPagination, loading: false });
          notificationController.error({
            message: error.message,
          });
        });
    },
    [isMounted, filters, form, selectedImportType],
  );

  useEffect(() => {
    if (!lastFetchedRef.current) {
      lastFetchedRef.current = true;
      fetch(initialPagination);
    }
  }, [fetch]);

  useEffect(() => {
    if (lastPathnameRef.current !== location.pathname) {
      lastPathnameRef.current = location.pathname;
      fetch(initialPagination);
    }
  }, [location.pathname, fetch]);

  const handleTableChange = (pagination: TablePaginationConfig) => {
    fetch({
      current: pagination.current || 1,
      pageSize: pagination.pageSize || 10,
    });
  };

  const handleViewRow = (id: number) => {
    navigate(`/view-import`, { state: { id: id } });
  };

  // const handleEditRow = (id: number) => {
  //   navigate(`/edit-import`, { state: { id: id } });
  // };

  const showTotal = (total: number, range: [number, number]): React.ReactNode => {
    return (
      <span style={{ fontSize: '14px', marginRight: '20px', color: '#adadad' }}>
        {`Showing ${range[0]} - ${range[1]} of ${total} Imports`}
      </span>
    );
  };

  const searchData: {
    name: string;
    placeholder: string;
    type: 'text' | 'dropdown';
    options?: Record<string, { label: string; value: string }[]>;
  }[] = [
    { name: 'search', placeholder: 'Search', type: 'text' },
    {
      name: 'status',
      placeholder: 'Status',
      type: 'dropdown',
      options: statusOptions ? { statusOptions: statusOptions } : {},
    },
  ];

  const columns: ColumnsType<ImportTableRow> = [
    {
      title: 'Job UID',
      dataIndex: 'code',
      key: 'code',
      width: '20%',
    },
    {
      title: 'Job Status',
      dataIndex: 'status',
      key: 'status',
      width: '20%',
    },
    {
      title: 'Imported Qty (KG)',
      dataIndex: 'imported_weightage',
      key: 'imported_weightage',
      width: '10%',
    },
    {
      title: 'Expiration Countdown',
      dataIndex: 'expire_date',
      key: 'expire_date',
      render: (text: string) => {
        const expirationDate = new Date(text);
        const now = new Date();
        const diffTime = expirationDate.getTime() - now.getTime();
        const daysLeft = Math.ceil(diffTime / (1000 * 3600 * 24));

        const isExpiringSoon = daysLeft <= 7;
        return <span style={{ color: isExpiringSoon ? 'red' : 'inherit' }}>{daysLeft} Days Remaining</span>;
      },
    },
    {
      title: 'Job Type',
      dataIndex: 'product_reg_type',
      key: 'product_reg_type',
      width: '20%',
    },
    {
      title: 'Actions',
      key: 'actions',
      width: '20%',
      render: (_, record) => {
        return (
          <Space>
            <Tooltip title="View">
              <Button
                type="text"
                onClick={() => handleViewRow(record.id)}
                icon={<EyeOutlined />}
                style={{ color: '#1890ff' }}
              />
            </Tooltip>

            {/* <Tooltip title="Edit">
              <Button
                type="text"
                onClick={() => handleEditRow(record.id)}
                icon={<EditOutlined />}
                style={{ color: '#52c41a' }}
              />
            </Tooltip> */}
          </Space>
        );
      },
    },
  ];

  return (
    <BaseCard id="basic-table" title={title} padding="1.25rem">
      <FilterComponent data={searchData} onFilter={setFilters} />
      <BaseTable
        columns={columns}
        dataSource={tableData.data}
        pagination={{
          current: tableData.pagination.current,
          pageSize: tableData.pagination.pageSize,
          total: tableData.pagination.total,
          showTotal,
        }}
        loading={tableData.loading}
        onChange={handleTableChange}
        scroll={{ x: true, scrollToFirstRowOnChange: true }}
      />
    </BaseCard>
  );
};

export default ImportTable;
